import React, { useEffect, useState } from "react";
import { Package, Plus, Save, Trash2, X, Check } from "lucide-react";
import { api, formatZAR } from "../api";
import { toast } from "sonner";

export default function Bundles() {
  const [bundles, setBundles] = useState([]);
  const [cats, setCats] = useState([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: "", description: "", items: [] });
  const [editingId, setEditingId] = useState("");
  const [editDraft, setEditDraft] = useState(null);

  const load = async () => {
    const [b, c] = await Promise.all([
      api.get("/task-bundles"),
      api.get("/task-categories"),
    ]);
    setBundles(b.data);
    setCats(c.data);
  };
  useEffect(() => { load(); }, []);

  const catName = id => cats.find(c => c.id === id)?.name || "—";

  const saveNew = async () => {
    if (!draft.name.trim() || draft.items.length === 0) {
      toast.error("Bundle needs a name and at least one task");
      return;
    }
    await api.post("/task-bundles", draft);
    toast.success("Bundle created");
    setDraft({ name: "", description: "", items: [] });
    setCreating(false);
    load();
  };

  const deleteBundle = async (b) => {
    if (!window.confirm(`Delete "${b.name}"?`)) return;
    await api.delete(`/task-bundles/${b.id}`);
    toast.success("Deleted");
    load();
  };

  const startEdit = (b) => {
    setEditingId(b.id);
    setEditDraft({ name: b.name, description: b.description || "", items: [...(b.items || [])] });
  };

  const saveEdit = async (b) => {
    await api.patch(`/task-bundles/${b.id}`, editDraft);
    toast.success("Bundle updated");
    setEditingId("");
    setEditDraft(null);
    load();
  };

  const toggleItem = (state, setState, catId) => {
    setState({
      ...state,
      items: state.items.some(i => i.task_category_id === catId)
        ? state.items.filter(i => i.task_category_id !== catId)
        : [...state.items, { task_category_id: catId, notes: "" }],
    });
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="np-label mb-2">Reusable task bundles</div>
          <h1 className="text-3xl font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>Task Bundles</h1>
          <p className="text-[#595959] mt-2">Pre-defined groups of tasks. Apply a bundle to a client in one click.</p>
        </div>
        {!creating && (
          <button data-testid="new-bundle-btn" onClick={() => setCreating(true)} className="bg-[#00ACB3] hover:bg-[#0F4F51] text-white font-semibold rounded-lg px-5 py-2.5 inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> New bundle
          </button>
        )}
      </div>

      {creating && (
        <div className="np-card mb-6 border-2 border-[#00ACB3]">
          <h2 className="text-lg font-bold text-[#011617] mb-3" style={{ fontFamily: "Manrope" }}>New bundle</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <input data-testid="new-bundle-name" value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} placeholder="Bundle name (e.g. Google Ads Monthly)" className="px-3 py-2.5 rounded-lg border border-[#CCCCCC] outline-none focus:border-[#00ACB3] bg-white" />
            <input data-testid="new-bundle-desc" value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} placeholder="Description (optional)" className="px-3 py-2.5 rounded-lg border border-[#CCCCCC] outline-none focus:border-[#00ACB3] bg-white" />
          </div>
          <div className="np-label mb-2">Tick tasks to include ({draft.items.length} selected)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto p-3 bg-[#F4F4F4] rounded-lg">
            {cats.map(c => {
              const checked = draft.items.some(i => i.task_category_id === c.id);
              return (
                <label key={c.id} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer ${checked ? "bg-[#E5FFFF]" : "bg-white hover:bg-[#E5FFFF]/40"}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggleItem(draft, setDraft, c.id)} data-testid={`new-bundle-cat-${c.id}`} />
                  <span className="text-sm font-semibold text-[#011617] flex-1 truncate">{c.name}</span>
                  <span className="text-xs text-[#595959]">{c.default_repeat} • {formatZAR(c.default_price)}</span>
                </label>
              );
            })}
          </div>
          <div className="flex gap-2 mt-4">
            <button data-testid="save-new-bundle" onClick={saveNew} className="bg-[#00ACB3] hover:bg-[#0F4F51] text-white font-semibold rounded-lg px-4 py-2.5 inline-flex items-center gap-2">
              <Save className="w-4 h-4" /> Save bundle
            </button>
            <button data-testid="cancel-new-bundle" onClick={() => { setCreating(false); setDraft({ name: "", description: "", items: [] }); }} className="border border-[#CCCCCC] hover:border-[#0F4F51] text-[#011617] font-semibold rounded-lg px-4 py-2.5 inline-flex items-center gap-2">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {bundles.map(b => {
          const isEditing = editingId === b.id;
          return (
            <div key={b.id} data-testid={`bundle-card-${b.id}`} className={`np-card ${isEditing ? "border-2 border-[#00ACB3]" : ""}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Package className="w-4 h-4 text-[#0F4F51] shrink-0" />
                  {isEditing ? (
                    <input value={editDraft.name} onChange={e => setEditDraft({ ...editDraft, name: e.target.value })} className="font-bold text-[#011617] text-base outline-none border-b border-[#CCCCCC] focus:border-[#00ACB3] bg-transparent flex-1" data-testid={`edit-name-${b.id}`} />
                  ) : (
                    <h3 className="font-bold text-[#011617] text-base truncate" style={{ fontFamily: "Manrope" }}>{b.name}</h3>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {isEditing ? (
                    <>
                      <button data-testid={`save-bundle-${b.id}`} onClick={() => saveEdit(b)} className="text-[#00ACB3] hover:text-[#0F4F51]"><Check className="w-4 h-4" /></button>
                      <button data-testid={`cancel-bundle-${b.id}`} onClick={() => { setEditingId(""); setEditDraft(null); }} className="text-[#595959] hover:text-[#011617]"><X className="w-4 h-4" /></button>
                    </>
                  ) : (
                    <>
                      <button data-testid={`edit-bundle-${b.id}`} onClick={() => startEdit(b)} className="text-xs font-semibold text-[#00ACB3] hover:text-[#0F4F51]">Edit</button>
                      <button data-testid={`delete-bundle-${b.id}`} onClick={() => deleteBundle(b)} className="text-[#991B1B] hover:text-[#7F1D1D]"><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
              </div>
              {isEditing ? (
                <input value={editDraft.description} onChange={e => setEditDraft({ ...editDraft, description: e.target.value })} placeholder="Description" className="text-xs text-[#595959] mb-3 outline-none border-b border-[#CCCCCC] focus:border-[#00ACB3] bg-transparent w-full" />
              ) : (
                <p className="text-xs text-[#595959] mb-3">{b.description || "—"}</p>
              )}
              <div className="np-label mb-1">Tasks ({(isEditing ? editDraft.items : b.items)?.length || 0})</div>
              {isEditing ? (
                <div className="grid grid-cols-1 gap-1 max-h-48 overflow-y-auto p-2 bg-[#F4F4F4] rounded-lg">
                  {cats.map(c => {
                    const checked = editDraft.items.some(i => i.task_category_id === c.id);
                    return (
                      <label key={c.id} className={`flex items-center gap-2 p-1.5 rounded cursor-pointer text-sm ${checked ? "bg-[#E5FFFF]" : "bg-white"}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleItem(editDraft, setEditDraft, c.id)} />
                        <span className="text-[#011617] flex-1 truncate">{c.name}</span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <ul className="text-sm text-[#011617] space-y-1">
                  {(b.items || []).map(it => (
                    <li key={it.task_category_id} className="px-2 py-1 rounded bg-[#F4F4F4]">• {catName(it.task_category_id)}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
